import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography, Button } from '@mui/material';

import { useAuthStore } from './stores/authStore';
import { apiClient } from './services/apiClient';
import { Loader } from './components/common/Loader';

import { HomePage } from './pages/HomePage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { ServicesPage } from './pages/ServicesPage';
import { DoctorsPage } from './pages/DoctorsPage';
import { ProfilePage } from './pages/ProfilePage';
import { DocumentsPage } from './pages/DocumentsPage';
import { ClinicPage } from './pages/ClinicPage';
import { RequestsPage } from './pages/RequestsPage';
import { ChildrenPage } from './pages/ChildrenPage';
import { SurveysPage } from './pages/SurveysPage';
import { PromotionsPage } from './pages/PromotionsPage';
import { BookingPage } from './pages/BookingPage';
import { VaccinationsPage } from './pages/VaccinationsPage';
import { RecommendationsPage, PetRecommendationsPage } from './pages/RecommendationsPage';
import { CabinetPage } from './pages/CabinetPage';
import { PaperRequestPage } from './pages/PaperRequestPage';
import { CertificateRequestPage } from './pages/CertificateRequestPage';
import { BalancePage } from './pages/BalancePage';
import { ReviewPage } from './pages/ReviewPage';
import { AppointmentPage } from './pages/AppointmentPage';
import { BeforeAfterPage } from './pages/BeforeAfterPage';
import { RecommendClinicPage } from './pages/RecommendClinicPage';

const tiktokFontFace = {
  '@font-face': [
    {
      fontFamily: 'TikTok Sans',
      src: "url('/fonts/TikTokSans-Regular.woff2') format('woff2')",
      fontWeight: 400,
      fontStyle: 'normal',
    },
    {
      fontFamily: 'TikTok Sans',
      src: "url('/fonts/TikTokSans-Medium.woff2') format('woff2')",
      fontWeight: 500,
      fontStyle: 'normal',
    },
    {
      fontFamily: 'TikTok Sans',
      src: "url('/fonts/TikTokSans-Bold.woff2') format('woff2')",
      fontWeight: 700,
      fontStyle: 'normal',
    },
  ],
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976FF',
      dark: '#1565E0',
      light: '#42A5F5',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4CAF50',
    },
    background: {
      default: '#F4F5F7',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#333333',
    },
    divider: '#ECECEC',
    error: {
      main: '#FF4D4F',
    },
    success: {
      main: '#52C41A',
    },
    warning: {
      main: '#FAAD14',
    },
  },
  typography: {
    fontFamily: '"TikTok Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h5: {
      fontSize: '22px',
      fontWeight: 600,
      lineHeight: '1.2',
    },
    h6: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '1.2',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '1.4',
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '1.6',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '1.4',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '1.4',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...tiktokFontFace,
        html: {
          paddingLeft: 'max(0px, env(safe-area-inset-left))',
          paddingRight: 'max(0px, env(safe-area-inset-right))',
          paddingTop: 'max(0px, env(safe-area-inset-top))',
          paddingBottom: 'max(0px, env(safe-area-inset-bottom))',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        body: {
          overflowX: 'hidden',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          borderRadius: '20px',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '14px',
          height: '48px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '14px',
          padding: '0 16px',
          transition: 'all 200ms ease',
          '&:active': {
            transform: 'scale(0.97)',
          },
        },
        contained: {
          boxShadow: '0 4px 20px rgba(25, 118, 255, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(25, 118, 255, 0.3)',
          },
        },
        outlined: {
          borderColor: '#E6E6E6',
          color: '#1A1A1A',
          '&:hover': {
            borderColor: '#1976FF',
            color: '#1976FF',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          backgroundColor: '#FFFFFF',
        },
        elevation0: {
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          height: '56px',
          backgroundColor: '#FFFFFF',
          color: '#1A1A1A',
          boxShadow: '0 1px 0 0 #ECECEC',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px',
          height: '56px',
          padding: '0 16px',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: '70px',
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: 'none',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          paddingBottom: '8px',
          paddingTop: '8px',
          color: '#6B7280',
          fontSize: '12px',
          '&.Mui-selected': {
            color: '#1976FF',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          backgroundColor: '#FFFFFF',
          height: '44px',
          '& fieldset': {
            borderColor: '#E6E6E6',
          },
          '&:hover fieldset': {
            borderColor: '#1976FF',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#1976FF',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          height: '24px',
          fontSize: '12px',
        },
        colorPrimary: {
          backgroundColor: '#E3F2FD',
          color: '#1976FF',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        backdrop: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      },
    },
  },
});

const ErrorScreen: React.FC<{ error: string; onRetry: () => void }> = React.memo(
  ({ error, onRetry }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" color="error" sx={{ marginBottom: 2 }}>
        Ошибка загрузки
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 3 }}>
        {error}
      </Typography>
      <Button variant="contained" onClick={onRetry}>
        Попробовать снова
      </Button>
    </Box>
  )
);

const MiniApp: React.FC = () => {
  const { initialize, isLoading, isAuthenticated, isInitialized, error, branchVersion } = useAuthStore();

  useEffect(() => {
    // При 401 (протух initData) — переинициализируем
    apiClient.onUnauthorized(() => {
      useAuthStore.getState().initialize();
    });

    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Показываем загрузку пока идёт инициализация
  if (!isInitialized || isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Loader message="Загрузка..." fullHeight />
      </ThemeProvider>
    );
  }

  // Ошибка аутентификации
  if (error || !isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorScreen
          error={error || 'Не удалось подключиться к серверу'}
          onRetry={initialize}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes key={branchVersion}>
          {/* Главная */}
          <Route path="/" element={<HomePage />} />

          {/* Акции */}
          <Route path="/promotions" element={<PromotionsPage />} />

          {/* О клинике (=> Новости, Контакты) */}
          <Route path="/clinic" element={<ClinicPage />} />
          <Route path="/news" element={<ClinicPage initialTab={1} />} />

          {/* Врачи => выбор специализации => врачи */}
          <Route path="/doctors" element={<DoctorsPage />} />

          {/* Кабинет (подменю) */}
          <Route path="/cabinet" element={<CabinetPage />} />

          {/* Мои записи */}
          <Route path="/appointments" element={<AppointmentsPage />} />

          {/* Профиль: данные */}
          <Route path="/profile" element={<ProfilePage />} />

          {/* Баланс */}
          <Route path="/balance" element={<BalancePage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/children" element={<ChildrenPage />} />
          <Route path="/surveys" element={<SurveysPage />} />
          <Route path="/vaccinations" element={<VaccinationsPage />} />
          <Route path="/recommendations" element={<RecommendationsPage />} />
          <Route path="/recommendations/:petId" element={<PetRecommendationsPage />} />

          {/* Заказ справки/сертификата */}
          <Route path="/paper-request" element={<PaperRequestPage />} />
          <Route path="/certificate-request" element={<CertificateRequestPage />} />
          <Route path="/requests" element={<RequestsPage />} />

          {/* Услуги */}
          <Route path="/services" element={<ServicesPage />} />

          {/* До/После */}
          <Route path="/before-after" element={<BeforeAfterPage />} />

          {/* Отзыв */}
          <Route path="/review" element={<ReviewPage />} />

          {/* Посоветовать клинику */}
          <Route path="/recommend-clinic" element={<RecommendClinicPage />} />

          {/* Выбор метода записи */}
          <Route path="/book-appointment" element={<AppointmentPage />} />

          {/* Запись (deprecated, но оставляем для совместимости) */}
          <Route path="/booking" element={<BookingPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default MiniApp;